import Helper from '@ember/component/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  peekMediaUrl,
  resolveMediaUrl,
} from '@linc-technologies/linced-common/utils/media-url';

// Resolves a media record's displayable URL. Peeks the cache synchronously
// first (no flash for a rawUrl or already-cached blob); falls back to async
// resolution on a cache miss. Pass rendition="avatar" to override the
// default 'original'.
export default class MediaUrl extends Helper {
  @service media;

  @tracked uri = null;

  // Snapshot of the in-flight resolve's args, to skip redundant resolves
  // and let a stale callback recognise itself.
  lastMedia = undefined;
  lastRendition = undefined;

  // Who `uri` belongs to -- a helper instance is reused across rows in
  // {{each}}/vertical-collection, so without this a stale row's url would
  // show until the new fetch lands.
  uriMedia = undefined;
  uriRendition = undefined;

  compute([media], { rendition = 'original' } = {}) {
    const cached = peekMediaUrl(media, rendition);
    if (cached !== undefined) {
      return cached;
    }

    if (media !== this.lastMedia || rendition !== this.lastRendition) {
      this.lastMedia = media;
      this.lastRendition = rendition;
      this.resolve(media, rendition);
    }

    // Must read `uri` every call to stay entangled, so a later resolve
    // re-renders.
    const uri = this.uri;
    return media === this.uriMedia && rendition === this.uriRendition
      ? uri
      : null;
  }

  resolve(media, rendition) {
    // Deferred a tick so committing `uri` never lands in the same render
    // pass that read it (Glimmer's backtracking assertion).
    Promise.resolve()
      .then(() => resolveMediaUrl(this.media, media, rendition))
      .then((url) => {
        if (this.isStale(media, rendition)) return;
        this.commit(media, rendition, url);
      })
      .catch((e) => {
        if (this.isStale(media, rendition)) return;
        console.error('failed to resolve media uri: ' + e);
        this.commit(media, rendition, null);
      });
  }

  commit(media, rendition, url) {
    this.uriMedia = media;
    this.uriRendition = rendition;
    this.uri = url;
  }

  isStale(media, rendition) {
    return (
      media !== this.lastMedia ||
      rendition !== this.lastRendition ||
      this.isDestroying ||
      this.isDestroyed
    );
  }
}
